import { QueryKey } from "@tanstack/react-query";
import { useWSQuery, WSQueryCache } from "@ws-react-query";
import flatten from "lodash/flatten";
import times from "lodash/times";
import { useState } from "react";
import {
  concurrentActions,
  WSServiceError
} from "../../../utils/serviceHelper";

const PAGE_SIZE = 500;
const CONCURRENCY = 5;

export function useFetchReportData<
  T,
  R extends { summary: { listSize: number }; data: T[] }
>(
  queryKey: QueryKey,
  request: (pageSize: number, pageNumber: number) => Promise<R>
) {
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [processedRowsCount, setProcessedRowsCount] = useState(0);

  const query = useWSQuery<T[], WSServiceError>(
    queryKey,
    async () => {
      // Reset
      setTotalRowsCount(0);
      setProcessedRowsCount(0);

      // Fetch first page
      const {
        summary: { listSize },
        data: firstPage
      } = await request(PAGE_SIZE, 1);

      // Update state with first page
      setTotalRowsCount(listSize);
      setProcessedRowsCount(listSize > PAGE_SIZE ? PAGE_SIZE : listSize);

      WSQueryCache.setQueryData(queryKey, firstPage);

      // Calculate total pages left to fetch
      const pageCount = Math.ceil(listSize / PAGE_SIZE) - 1;

      // Fetch remaining pages concurrently
      const pages = await concurrentActions(
        times(pageCount).map(
          pageNumber => async () =>
            (
              await request(PAGE_SIZE, pageNumber + 2)
            ).data
        ),
        {
          concurrentLimit: CONCURRENCY,
          onActionFinished: index => {
            if (index === pageCount - 1) {
              setProcessedRowsCount(prev => prev + (listSize % PAGE_SIZE));
            } else {
              setProcessedRowsCount(prev => prev + PAGE_SIZE);
            }
          }
        }
      );

      // Return all pages
      return flatten([firstPage, ...pages]);
    },

    {
      refetchOnMount: false,
      retry: false,
      enabled: false
    }
  );

  return {
    query,
    totalRowsCount,
    processedRowsCount
  };
}
